import React, { useEffect, useRef } from "react";
// Prefer package import. If you must use the ESM URL, use:
// import * as d3 from "https://cdn.jsdelivr.net/npm/d3@7/+esm";
import * as d3 from "d3";

export default function CircularStepper({
  step = 1,
  showStep = false,
  total = 8,
  size = 180,
  strokeWidth = 10,
  startColor = "#ff4d4d",
  endColor = "#8b0000",
  text,
  carInfo,
  showCar,
}) {
  const svgRef = useRef(null);
  const progressGroupRef = useRef(null);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const progress = Math.max(0, Math.min(step / total, 1)); // clamp [0,1]

  useEffect(() => {
    const g = d3.select(progressGroupRef.current);

    // Clear previous segments
    g.selectAll("*").remove();

    // If no progress, nothing to draw
    if (progress <= 0 || !isFinite(progress)) return;

    const cx = size / 2;
    const cy = size / 2;
    const r = radius;

    // Build a centerline arc path for the current progress (starting from 0 radians).
    const theta = progress * 2 * Math.PI;
    const x0 = cx + r;
    const y0 = cy;
    const x1 = cx + r * Math.cos(theta);
    const y1 = cy + r * Math.sin(theta);

    // For a full circle, compose two arcs (0→π and π→2π) to ensure path length > 0
    let d;
    if (progress >= 1) {
      const xMid = cx - r; // angle π
      const yMid = cy;
      d = `M ${x0} ${y0} A ${r} ${r} 0 1 1 ${xMid} ${yMid} A ${r} ${r} 0 1 1 ${x0} ${y0}`;
    } else {
      const largeArc = theta > Math.PI ? 1 : 0;
      d = `M ${x0} ${y0} A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`;
    }

    // Create a hidden base path to sample points along the arc
    const basePath = g
      .append("path")
      .attr("d", d)
      .attr("fill", "none")
      .attr("stroke", "none");

    // Color scale from startColor → endColor
    const color = d3
      .scaleSequential()
      .domain([0, 1])
      .interpolator(d3.interpolateRgb(endColor,startColor));

    // Helper functions from your Spinner (adapted)
    function samples(pathNode, precision = 4) {
      // precision ~ step along the path in px (smaller => smoother)
      // choose based on circumference to keep a consistent smoothness
      const n = pathNode.getTotalLength();
      const t = [0];
      let i = 0;
      const dt = Math.max(2, Math.min(6, circumference / 200)); // auto precision

      while ((i += dt) < n) t.push(i);
      t.push(n);

      return t.map((t) => {
        const p = pathNode.getPointAtLength(t);
        const a = [p.x, p.y];
        a.t = t / n;
        return a;
      });
    }

    function quads(points) {
      return d3.range(points.length - 1).map((i) => {
        const a = [points[i - 1], points[i], points[i + 1], points[i + 2]];
        a.t = (points[i].t + points[i + 1].t) / 2;
        return a;
      });
    }

    function lineJoin(p0, p1, p2, p3, width) {
      const u12 = perp(p1, p2);
      const r = width / 8;

      let a = [p1[0] + u12[0] * r, p1[1] + u12[1] * r];
      let b = [p2[0] + u12[0] * r, p2[1] + u12[1] * r];
      let c = [p2[0] - u12[0] * r, p2[1] - u12[1] * r];
      let d = [p1[0] - u12[0] * r, p1[1] - u12[1] * r];

      if (p0) {
        const u01 = perp(p0, p1);
        const e = [p1[0] + u01[0] + u12[0], p1[1] + u01[1] + u12[1]];
        a = lineIntersect(p1, e, a, b);
        d = lineIntersect(p1, e, d, c);
      }

      if (p3) {
        const u23 = perp(p2, p3);
        const e = [p2[0] + u23[0] + u12[0], p2[1] + u23[1] + u12[1]];
        b = lineIntersect(p2, e, a, b);
        c = lineIntersect(p2, e, d, c);
      }

      return `M${a}L${b} ${c} ${d}Z`;
    }

    function lineIntersect(a, b, c, d) {
      const x1 = c[0],
        x3 = a[0],
        x21 = d[0] - x1,
        x43 = b[0] - x3;
      const y1 = c[1],
        y3 = a[1],
        y21 = d[1] - y1,
        y43 = b[1] - y3;

      const ua =
        (x43 * (y1 - y3) - y43 * (x1 - x3)) / (y43 * x21 - x43 * y21);

      return [x1 + ua * x21, y1 + ua * y21];
    }

    function perp(p0, p1) {
      const u01x = p0[1] - p1[1];
      const u01y = p1[0] - p0[0];
      const u01d = Math.sqrt(u01x * u01x + u01y * u01y);
      return [u01x / u01d, u01y / u01d];
    }

    // Build quads on the path
    const pts = samples(basePath.node());
    basePath.remove(); // we only keep the shaded segments

    // width param trick: r = width/8 => we want r = strokeWidth/2 => width = 4*strokeWidth
    const widthParam = 4 * strokeWidth;

    g.selectAll("path.progress-segment")
      .data(quads(pts))
      .enter()
      .append("path")
      .attr("class", "progress-segment")
      .attr("d", (q) => lineJoin(q[0], q[1], q[2], q[3], widthParam))
      .style("fill", (q) => color(q.t))
      .style("stroke", (q) => color(q.t))
      .style("shape-rendering", "geometricPrecision");

    // Optional: fade-in transition for updates (uncomment to animate)
    g.selectAll("path.progress-segment")
      .style("opacity", 0)
      .transition()
      .duration(500)
      .style("opacity", 1);
  }, [progress, size, strokeWidth, startColor, endColor, circumference, radius]);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* SVG */}
      <svg 
      ref={svgRef} 
      width={size} 
      height={size} 
      viewBox={`${-strokeWidth} ${-strokeWidth} ${size + strokeWidth * 2} ${size + strokeWidth * 2}`}
      className="rotate-[-90deg]" >
        {/* Background Circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* D3-rendered progress (gradient along the arc) */}
        <g ref={progressGroupRef} />
        <g className="step-separators">
          {Array.from({ length: total }).map((_, i) => {
            if (i === 0 && step !== 8) return null;
            const angle = (i / total) * 2 * Math.PI;
            const cx = size / 2;
            const cy = size / 2;

            const innerR = radius - strokeWidth / 2;
            const outerR = radius + strokeWidth / 2;

            const x1 = cx + innerR * Math.cos(angle);
            const y1 = cy + innerR * Math.sin(angle);
            const x2 = cx + outerR * Math.cos(angle);
            const y2 = cy + outerR * Math.sin(angle);

            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="white"
                strokeOpacity={0.8}
                strokeWidth={4}
                strokeLinecap="round"
              />
            );
          })}
        </g>
      </svg>

      {/* Center Content */}
      {!showCar ? (
        <div className="absolute flex flex-col items-center justify-center text-center max-w-[120] p-3 mx-3">
          {showStep && <p className="text-xs text-grenite">Βήμα {step}/{total}</p>}
          <p className="font-bold text-gray-800 leading-tight pt-2">{text}</p>
          {carInfo?.quantity > 1 && <p className="text-xs text-gray-800 leading-tight pt-2">Ποσότητα παραγγελίας:</p>}
          {carInfo?.quantity > 1 && <p className="text-xs text-gray-800 leading-tight pt-1">{carInfo?.quantity} αυτοκίνητα</p>}
        </div>
      ) : (
        <div className="absolute flex flex-col items-center justify-center text-center max-w-[120]">
          <div className="w-[190px]">
            <img
              alt={carInfo?.carTitle}
              src={`https://cdn.imagin.studio/getImage?customer=avis-gr&&make=${ 
                carInfo?.MakeName ?? carInfo?.make ?? carInfo?.carTitle?.split(' ')[0] }&modelFamily=${carInfo?.ModelName ?? carInfo?.model ?? carInfo?.carTitle?.split(' ')[1]
              }&modelVariant=${carInfo?.ModelName || carInfo?.model || carInfo?.carTitle?.split(' ').slice(1).join(' ')}&modelYear=${
                carInfo?.ModelYear
              }&powerTrain=hybrid&trim=eu&zoomType=fullScreen&width=800&angle=28`}
            />
          </div>
          <p className="text-sm text-black">
            {carInfo?.carTitle ??
              `${carInfo?.MakeName?.toUpperCase() || ""} ${carInfo?.ModelName || ""}`}
          </p>
        </div>
      )}
    </div>
  );
}